import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import type {HeaderAction} from '@github-ui/layout-primitives/use-header-actions-overflow'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useInfiniteQuery} from '@github-ui/react-query'
import {useMatch, useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {testIdProps} from '@github-ui/test-id-props'
import {useSearchParams} from '@github-ui/use-navigate'
import {LinkIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {LinkProjectDialog, LinkProjectDialogPanel} from '../components/projects/LinkProjectDialog'
import {NewProjectForm} from '../components/projects/NewProjectForm'
import {getProjectListLoadingState} from '../components/projects/project-list-loading-state'
import {
  DEFAULT_PROJECT_SORT,
  isProjectSortValue,
  type ProjectSortValue,
} from '../components/projects/project-sort-options'
import {ProjectList} from '../components/projects/ProjectList'
import {ProjectsActions} from '../components/projects/ProjectsActions'
import {ProjectSearchInput} from '../components/projects/ProjectSearchInput'
import {ProjectsTemplatesToggle} from '../components/projects/ProjectsTemplatesToggle'
import styles from '../components/projects/RepositoryProjects.module.css'
import type {NewProjectAction} from '../components/projects/types'
import {useCachedProjectsOwnerContext} from '../components/projects/use-cached-projects-owner-context'
import {useProjectsDismissibleNotices} from '../components/projects/use-projects-dismissible-notices'
import {useShowTemplatesIndex} from '../components/projects/use-show-templates-index'
import {WelcomeBanner} from '../components/projects/WelcomeBanner'
import {WelcomeSplashDialog} from '../components/projects/WelcomeSplashDialog'
import {renderIssuesSidebar} from '../components/sidebar/render-issues-sidebar'
import {projectsInfiniteQueryOptions} from '../queries/projects'
import {AnalyticsWrapper} from './AnalyticsWrapper'

export function RepositoryProjectsPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const state = searchParams.get('state') === 'closed' ? 'closed' : 'open'
  const query = searchParams.get('q') ?? ''
  const sortParam = searchParams.get('sort')
  const sort = isProjectSortValue(sortParam) ? sortParam : DEFAULT_PROJECT_SORT
  // URL is the single source of truth for which view we're on, mirroring how `sort`
  // and `q` work.
  const isTemplatesView = useMatch('/:owner/:repo/projects/templates') !== null
  const mode: 'projects' | 'templates' = isTemplatesView ? 'templates' : 'projects'
  const baseProjectsUrl = useMemo(() => `/${owner}/${repo}/projects`, [owner, repo])
  const {isBrowser} = useRuntimeEnvironment()

  const {data, isPending, isError, isFetchingNextPage, isPlaceholderData, hasNextPage, fetchNextPage} =
    useInfiniteQuery({
      ...projectsInfiniteQueryOptions({
        owner,
        repo,
        state,
        query: query || undefined,
        sort,
        isTemplateView: isTemplatesView,
      }),
      // No dehydration boundary on this route, so don't start a server fetch we'd abandon.
      enabled: isBrowser,
    })
  // Read the templates flag from any cached sibling response so the toggle stays
  // mounted across the remount when toggling Projects ↔ Templates.
  const cachedShowTemplatesIndex = useShowTemplatesIndex(owner, repo)
  // Same trick for the header CTA's owner-context fields: without this the New project
  // button would unmount during the in-flight pending state on the new route.
  const cachedOwnerContext = useCachedProjectsOwnerContext(owner, repo)
  // Local + persistent dismissal state for the welcome onboarding. Hoisted above
  // the early return below to keep hook call order stable across renders.
  const {isLocallyDismissed, dismissNotice} = useProjectsDismissibleNotices()
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const handleQueryChange = useCallback(
    (next: string) => {
      const trimmed = next.trim()
      const params = new URLSearchParams(searchParams)
      if (trimmed) {
        params.set('q', trimmed)
      } else {
        params.delete('q')
      }
      setSearchParams(params, {replace: true, preventAutofocus: true})
    },
    [searchParams, setSearchParams],
  )

  const handleSortChange = useCallback(
    (next: ProjectSortValue) => {
      const params = new URLSearchParams(searchParams)
      if (next === DEFAULT_PROJECT_SORT) {
        params.delete('sort')
      } else {
        params.set('sort', next)
      }
      setSearchParams(params, {replace: true, preventAutofocus: true})
    },
    [searchParams, setSearchParams],
  )

  const firstPage = data?.pages[0]
  const projects = data?.pages.flatMap(page => page.projects) ?? []
  const showTemplatesToggle = (firstPage?.showTemplatesIndex ?? cachedShowTemplatesIndex) === true
  const templatesToggle = showTemplatesToggle ? <ProjectsTemplatesToggle mode={mode} baseUrl={baseProjectsUrl} /> : null
  const viewerCanPush = firstPage?.viewerCanPush ?? cachedOwnerContext?.viewerCanPush ?? false
  const newProjectAction = firstPage?.newProjectAction ?? cachedOwnerContext?.newProjectAction ?? null

  const dismissedNotices = firstPage?.dismissedNotices
  const splashDismissed =
    (dismissedNotices?.repoProjectsBetaSplash ?? true) ||
    (dismissedNotices?.projectsBetaSplash ?? true) ||
    isLocallyDismissed('repo_projects_beta_splash')
  const bannerDismissed =
    (dismissedNotices?.repoProjectsBetaBanner ?? true) ||
    (dismissedNotices?.projectsBetaBanner ?? true) ||
    isLocallyDismissed('repo_projects_beta_banner')

  const showWelcomeSplash = !splashDismissed
  const showWelcomeBanner = splashDismissed && !bannerDismissed

  const trailingHeaderContent = (
    <div className={styles.headerTrailing}>
      {templatesToggle}
      {viewerCanPush && <LinkProjectDialog owner={owner} repo={repo} isTemplateView={isTemplatesView} />}
      {newProjectAction && (
        <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
          <Button type="submit" variant="primary">
            New project
          </Button>
        </NewProjectForm>
      )}
    </div>
  )

  const projectsTitle = isTemplatesView ? 'Templates' : 'Projects'
  const headerActions = sidebarLayoutEnabled ? (
    <ProjectsHeaderActions
      owner={owner}
      repo={repo}
      isTemplatesView={isTemplatesView}
      templatesToggle={templatesToggle}
      viewerCanPush={viewerCanPush}
      newProjectAction={newProjectAction}
    />
  ) : undefined

  return (
    <AnalyticsWrapper category="Repository Projects Index">
      <RepoIssuesPageLayout
        dialogTitle="Projects"
        title={sidebarLayoutEnabled ? projectsTitle : undefined}
        actions={headerActions}
        renderSidebar={renderIssuesSidebar}
      >
        <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
          <ErrorBoundary
            boundaryName="RepositoryProjectsPage"
            fallback={<p role="alert">Something went wrong loading projects.</p>}
          >
            <ProjectsActions heading={projectsTitle} trailing={trailingHeaderContent} />
            {showWelcomeSplash && (
              <WelcomeSplashDialog onDismiss={() => void dismissNotice('repo_projects_beta_splash')} />
            )}
            {showWelcomeBanner && <WelcomeBanner onDismiss={() => void dismissNotice('repo_projects_beta_banner')} />}
            <ProjectSearchInput query={query} onQueryChange={handleQueryChange} />
            {isError ? (
              <p role="alert">Something went wrong loading projects. Please try again.</p>
            ) : (
              // Always render `ProjectList` so the toolbar stays mounted across the
              // cross-route remount when toggling Projects ↔ Templates.
              <ProjectList
                projects={projects}
                openCount={firstPage?.openCount ?? 0}
                closedCount={firstPage?.closedCount ?? 0}
                hasNextPage={hasNextPage}
                onLoadMore={() => {
                  void fetchNextPage()
                }}
                viewerCanPush={viewerCanPush}
                owner={owner}
                repo={repo}
                newProjectAction={newProjectAction}
                query={query}
                state={state}
                sort={sort}
                onSortChange={handleSortChange}
                loadingState={getProjectListLoadingState({isPending, isPlaceholderData, isFetchingNextPage})}
                isTemplateView={isTemplatesView}
              />
            )}
          </ErrorBoundary>
        </div>
      </RepoIssuesPageLayout>
    </AnalyticsWrapper>
  )
}

interface ProjectsHeaderActionsProps {
  owner: string
  repo: string
  isTemplatesView: boolean
  templatesToggle: React.ReactNode
  viewerCanPush: boolean
  newProjectAction: NewProjectAction | null
}

function ProjectsHeaderActions({
  owner,
  repo,
  isTemplatesView,
  templatesToggle,
  viewerCanPush,
  newProjectAction,
}: ProjectsHeaderActionsProps) {
  const linkLabel = isTemplatesView ? 'Link a template' : 'Link a project'
  const [linkOpen, setLinkOpen] = useState(false)
  // SelectPanel returns focus to anchorRef on close; point it at whichever
  // trigger opened the panel so focus return follows the user.
  const linkAnchorRef = useRef<HTMLElement | null>(null)
  const inlineLinkTriggerRef = useRef<HTMLButtonElement>(null)
  const overflowTriggerRef = useRef<HTMLButtonElement>(null)

  const openLinkFromInline = useCallback(() => {
    linkAnchorRef.current = inlineLinkTriggerRef.current
    setLinkOpen(true)
  }, [])

  const openLinkFromMenu = useCallback(() => {
    // The ActionList.Item unmounts when the kebab menu closes, so anchor focus
    // return to the still-mounted kebab IconButton instead.
    linkAnchorRef.current = overflowTriggerRef.current ?? inlineLinkTriggerRef.current
    setLinkOpen(true)
  }, [])

  const closeLink = useCallback(() => setLinkOpen(false), [])

  const actions = useMemo<HeaderAction[]>(() => {
    const list: HeaderAction[] = []
    if (templatesToggle) {
      // SegmentedControl already collapses to a dropdown at narrow; don't nest it in the kebab.
      list.push({
        key: 'templates-toggle',
        alwaysInline: true,
        renderInline: () => templatesToggle,
        renderMenuItem: () => null,
      })
    }
    if (viewerCanPush) {
      list.push({
        key: 'link-project',
        renderInline: () => (
          <Button
            ref={inlineLinkTriggerRef}
            leadingVisual={LinkIcon}
            onClick={openLinkFromInline}
            aria-haspopup="dialog"
            aria-expanded={linkOpen}
            {...testIdProps('link-project-trigger')}
          >
            {linkLabel}
          </Button>
        ),
        renderMenuItem: () => (
          <ActionList.Item onSelect={openLinkFromMenu}>
            <ActionList.LeadingVisual>
              <LinkIcon />
            </ActionList.LeadingVisual>
            {linkLabel}
          </ActionList.Item>
        ),
      })
    }
    if (newProjectAction) {
      list.push({
        key: 'new-project',
        alwaysInline: true,
        renderInline: () => (
          <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
            <Button type="submit" variant="primary">
              New project
            </Button>
          </NewProjectForm>
        ),
        renderMenuItem: () => null,
      })
    }
    return list
  }, [
    templatesToggle,
    viewerCanPush,
    newProjectAction,
    owner,
    repo,
    linkLabel,
    linkOpen,
    openLinkFromInline,
    openLinkFromMenu,
  ])

  return (
    <>
      <FilteredListPageLayout.HeaderActions
        menuAriaLabel="More project actions"
        actions={actions}
        overflowTriggerRef={overflowTriggerRef}
      />
      {linkOpen && (
        <LinkProjectDialogPanel
          owner={owner}
          repo={repo}
          isTemplateView={isTemplatesView}
          anchorRef={linkAnchorRef}
          onClose={closeLink}
        />
      )}
    </>
  )
}
